import { useState, useRef } from "react";
import {
  applyHotelRefund,
  applyTourRefund,
  applyCarRefund,
  applyGuideRefund,
} from "@/api/afterSale";
import { applyGroupTourRefund } from "@/pages/route-detail-product/api/order";
import { uploadImage } from "@/api/upload";
import { toastError, toastSuccess } from "@/utils/toast";

/** 金额转「分」整数，避免浮点比较误差 */
function toCents(amount: number): number {
  return Math.round(amount * 100);
}

/** 钳制后的金额写回输入框（去掉多余小数位） */
function amountToInputString(amount: number): string {
  const cents = toCents(amount);
  const whole = cents / 100;
  if (cents % 100 === 0) return String(whole);
  return (cents / 100).toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

interface AfterSaleModalProps {
  visible: boolean;
  onClose: () => void;
  productName?: string;
  orderId?: number;
  orderType?: string;
  orderAmount?: number;
  onSubmitSuccess?: () => void;
}

export default function AfterSaleModal({
  visible,
  onClose,
  productName,
  orderId,
  orderType,
  orderAmount,
  onSubmitSuccess,
}: AfterSaleModalProps) {
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [refundAmount, setRefundAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!visible) return null;

  const maxRefundableCents =
    orderAmount != null &&
    Number.isFinite(orderAmount) &&
    orderAmount > 0
      ? toCents(orderAmount)
      : null;

  const reasons = [
    "服务与描述不符",
    "导游/司机态度差",
    "行程缺失内容",
    "安全问题",
    "设施损坏/安全隐患",
    "费用纠纷",
    "其他原因",
  ];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setUploadedImages((prev) => [...prev, url]);
    } catch (err: any) {
      toastError(err?.message || "图片上传失败");
    } finally {
      setUploading(false);
    }
  };

  const canSubmit = !!(
    selected &&
    reason.trim() &&
    refundAmount &&
    !amountError &&
    !submitting &&
    !uploading
  );

  const handleSubmit = async () => {
    if (!canSubmit || !orderId) return;
    const refundCents = toCents(parseFloat(refundAmount));
    if (
      maxRefundableCents != null &&
      (Number.isNaN(refundCents) || refundCents > maxRefundableCents)
    ) {
      setRefundAmount(amountToInputString(maxRefundableCents / 100));
      setAmountError("");
      toastError(`退款金额不能超过订单金额 ¥${orderAmount!.toLocaleString()}`);
      return;
    }
    setSubmitting(true);
    try {
      const apply_img = uploadedImages.join(",");
      const refund_money = refundCents / 100;
      let res;
      if (orderType === "hotel") {
        res = await applyHotelRefund({
          order_id: orderId,
          apply_reson: selected!,
          apply_desc: reason.trim(),
          refund_money,
          apply_img,
        });
      } else if (orderType === "tour") {
        res = await applyGroupTourRefund({
          order_id: orderId,
          apply_reason: selected!,
          apply_desc: reason.trim(),
          refund_money,
          apply_img,
        });
      } else if (orderType === "car") {
        res = await applyCarRefund({
          order_id: orderId,
          apply_reason: selected!,
          apply_desc: reason.trim(),
          refund_money,
          apply_img,
        });
      } else if (orderType === "guide") {
        res = await applyGuideRefund({
          id: orderId,
          reason: `${selected}${reason.trim() ? `：${reason.trim()}` : ""}`,
        });
      }
      toastSuccess(res?.message ?? "售后申请提交成功");
      setSubmitted(true);
      onSubmitSuccess?.();
    } catch (err: unknown) {
      console.error("[AfterSale] 提交失败:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-t-3xl w-full"
        style={{
          maxWidth: 480,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 flex-shrink-0" />
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F4F5F9" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">申请售后</span>
          <div className="w-8" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {submitted ? (
            <div className="flex flex-col items-center py-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#EFF6FF" }}
              >
                <i
                  className="ri-customer-service-2-line"
                  style={{ color: "#2563EB", fontSize: 32 }}
                />
              </div>
              <div className="text-base font-bold text-gray-900 mb-1">
                售后申请已提交
              </div>
              <div className="text-xs text-gray-500 text-center mb-3">
                服务商将尽快处理您的申请
              </div>
              <div
                className="p-3 rounded-2xl w-full"
                style={{ background: "#F8F8F8" }}
              >
                {productName && (
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-400">售后产品</span>
                    <span className="font-medium text-gray-700">
                      {productName}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-400">售后原因</span>
                  <span className="font-medium text-gray-700">{selected}</span>
                </div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-400">退款金额</span>
                  <span className="font-bold" style={{ color: "#EF4444" }}>
                    ¥{parseFloat(refundAmount).toLocaleString()}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="mt-5 px-8 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
                style={{ background: "#2563EB" }}
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              <div
                className="mb-4 p-3 rounded-2xl"
                style={{ background: "#F0F4FF" }}
              >
                <p className="text-xs text-gray-500 leading-relaxed">
                  <i
                    className="ri-information-line mr-1"
                    style={{ color: "#2563EB" }}
                  />
                  请如实填写售后信息并上传凭证
                </p>
              </div>
              {productName && (
                <div
                  className="mb-4 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-800 flex items-center gap-2"
                  style={{
                    background: "#F8F8F8",
                    border: "1.5px solid #F0F0F0",
                  }}
                >
                  <i
                    className="ri-checkbox-circle-fill flex-shrink-0"
                    style={{ color: "#2563EB", fontSize: 14 }}
                  />
                  <span>{productName}</span>
                </div>
              )}
              <div className="text-xs font-semibold text-gray-700 mb-2">
                售后原因 <span className="text-red-500">*</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {reasons.map((r) => (
                  <button
                    key={r}
                    onClick={() => setSelected(r)}
                    className="px-3 py-2 rounded-xl text-xs cursor-pointer font-medium whitespace-nowrap transition-all"
                    style={{
                      background: selected === r ? "#EFF6FF" : "#F4F5F9",
                      color: selected === r ? "#2563EB" : "#374151",
                      border:
                        selected === r
                          ? "1.5px solid #BFDBFE"
                          : "1.5px solid transparent",
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <div className="text-xs font-semibold text-gray-700 mb-2">
                问题描述 <span className="text-red-500">*</span>
              </div>
              <textarea
                className="w-full rounded-2xl p-3 text-xs resize-none outline-none"
                style={{
                  background: "#F8F8F8",
                  border: "1.5px solid #F0F0F0",
                  minHeight: 80,
                  color: "#374151",
                }}
                placeholder="请详细描述问题..."
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="flex justify-end text-xs text-gray-400 mt-1 mb-4">
                {reason.length}/500
              </div>
              <div className="text-xs font-semibold text-gray-700 mb-2">
                申请退款金额 <span className="text-red-500">*</span>
                {orderAmount && (
                  <span className="text-gray-400 font-normal ml-1">
                    （订单金额 ¥{orderAmount.toLocaleString()}）
                  </span>
                )}
              </div>
              <input
                type="number"
                inputMode="decimal"
                value={refundAmount}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setRefundAmount("");
                    setAmountError("");
                    return;
                  }
                  if (raw.startsWith("-")) return;

                  const n = parseFloat(raw);
                  if (Number.isNaN(n)) {
                    setAmountError("无效");
                    return;
                  }
                  if (n < 0) {
                    setRefundAmount("");
                    setAmountError("无效");
                    return;
                  }

                  if (maxRefundableCents != null) {
                    const cents = toCents(n);
                    if (cents > maxRefundableCents) {
                      setRefundAmount(
                        amountToInputString(maxRefundableCents / 100),
                      );
                      setAmountError("");
                      return;
                    }
                  }

                  // 允许输入「0.」等未完成的小数
                  if (
                    n === 0 &&
                    raw.includes(".") &&
                    /^\d*\.?\d*$/.test(raw)
                  ) {
                    setRefundAmount(raw);
                    setAmountError("");
                    return;
                  }

                  if (n <= 0) {
                    setRefundAmount(raw);
                    setAmountError("无效");
                    return;
                  }

                  setRefundAmount(raw);
                  setAmountError("");
                }}
                placeholder={
                  orderAmount
                    ? `最多¥${orderAmount.toLocaleString()}`
                    : "请输入"
                }
                className="w-full px-3.5 py-2.5 rounded-xl text-sm outline-none mb-4"
                style={{
                  background: "#F8F8F8",
                  border: amountError
                    ? "1.5px solid #EF4444"
                    : "1.5px solid #F0F0F0",
                  color: "#374151",
                }}
                min={0}
                max={orderAmount}
                step={0.01}
              />
              <div className="text-xs font-semibold text-gray-700 mb-2">
                上传凭证图片{" "}
                <span className="text-gray-400 font-normal">
                  （可选，最多6张）
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-1">
                {uploadedImages.map((img, i) => (
                  <div
                    key={i}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ width: 72, height: 60 }}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() =>
                        setUploadedImages((p) => p.filter((_, j) => j !== i))
                      }
                      className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full cursor-pointer"
                      style={{ background: "rgba(0,0,0,0.55)" }}
                    >
                      <i
                        className="ri-close-line text-white"
                        style={{ fontSize: 10 }}
                      />
                    </button>
                  </div>
                ))}
                {uploadedImages.length < 6 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex flex-col items-center justify-center rounded-2xl cursor-pointer"
                    style={{
                      width: 72,
                      height: 60,
                      background: "#F4F5F9",
                      border: "1.5px dashed #D1D5DB",
                    }}
                  >
                    {uploading ? (
                      <>
                        <i
                          className="ri-loader-4-line animate-spin"
                          style={{ color: "#9CA3AF", fontSize: 18 }}
                        />
                        <span
                          style={{
                            fontSize: 10,
                            color: "#9CA3AF",
                            marginTop: 2,
                          }}
                        >
                          上传中...
                        </span>
                      </>
                    ) : (
                      <>
                        <i
                          className="ri-add-line"
                          style={{ color: "#9CA3AF", fontSize: 18 }}
                        />
                        <span
                          style={{
                            fontSize: 10,
                            color: "#9CA3AF",
                            marginTop: 2,
                          }}
                        >
                          添加图片
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        {!submitted && (
          <div
            className="px-5 py-3 flex-shrink-0"
            style={{ borderTop: "1px solid #F5F5F5", background: "#fff" }}
          >
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{
                background: canSubmit ? "#2563EB" : "#F0F0F0",
                color: canSubmit ? "#fff" : "#9CA3AF",
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin" />
                  提交中...
                </span>
              ) : (
                "提交售后申请"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
